/**
 * Drift warnings for the kanban's recent chats (utils/driftModel.js): `{ [sessionId]: drift }`. Asks each project's
 * changes once per minute at most, for the recent (not running) chats of the last week whose project is known.
 * `projectOf(session)` names a chat's project (null: skipped).
 */
import { useEffect, useMemo, useState } from 'react';
import { getChanges } from '../services/CollabService';
import { driftFor } from '../utils/driftModel';

const WEEK_MS = 7 * 24 * 3600 * 1000;
const MAX_PROJECTS = 8;
const REFRESH_MS = 60000;

export default function useSessionDrift(sessions, activeIds, projectOf) {
  const [answers, setAnswers] = useState({});
  const candidates = useMemo(() => (sessions || []).filter((session) => (
    !activeIds?.has?.(session.id) && !session.state?.archived && Date.now() - Number(session.lastUpdateTime) * 1000 < WEEK_MS && projectOf(session)
  )), [sessions, activeIds, projectOf]);
  const projectKey = useMemo(() => [...new Set(candidates.map(projectOf))].slice(0, MAX_PROJECTS).sort().join(','), [candidates, projectOf]);

  useEffect(() => {
    if (!projectKey) { return undefined; }
    let cancelled = false;
    const load = async () => {
      const ids = projectKey.split(',');
      const results = await Promise.allSettled(ids.map((id) => getChanges(id, { since: new Date(Date.now() - WEEK_MS).toISOString(), limit: 300 })));
      if (cancelled) { return; }
      setAnswers((prev) => {
        const next = { ...prev };
        ids.forEach((id, index) => { if (results[index].status === 'fulfilled') { next[id] = results[index].value; } });
        return next;
      });
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [projectKey]);

  return useMemo(() => {
    const out = {};
    candidates.forEach((session) => {
      const drift = driftFor(session, answers[projectOf(session)]);
      if (drift) { out[session.id] = drift; }
    });
    return out;
  }, [candidates, answers, projectOf]);
}
